import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  useMemo,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../_metronic/layout/core'
import {WithChildren} from '../../_metronic/helpers'
import { AuthModel } from '../models/auth/auth.model'
import { UserModel } from '../models/user/user.model'
import { logoff } from '../services/auth.service'
import {
  getAuth,
  setAuth as setStoredAuth,
  removeAuth,
  getSessionExpiredMessage,
  setSessionExpiredMessage as setStoredSessionExpiredMessage,
  removeSessionExpiredMessage,
  isTokenExpired,
} from '../utils/auth.utils'
import { getUserDetails } from '../services/user.service'
import { useInactivityTimer } from '../utils/useInactivityTimer.utils'

const SESSION_EXPIRED_INACTIVITY_MESSAGE =
  'Your session has expired due to inactivity. Please log in again.'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  sessionExpiredMessage: string | undefined
  logoutDueToInactivity: () => void
}

const initAuthContextPropsState = {
  auth: getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  sessionExpiredMessage: undefined,
  logoutDueToInactivity: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | undefined>(
    () => {
      const message = getSessionExpiredMessage()
      removeSessionExpiredMessage()
      return message
    }
  )

  const saveAuth = useCallback((newAuth: AuthModel | undefined) => {
    setAuth(newAuth)
    if (newAuth) {
      setStoredAuth(newAuth)
      setSessionExpiredMessage(undefined)
    } else {
      removeAuth()
    }
  }, [])

  const logout = useCallback(() => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    logoff();
  }, [saveAuth])

  const logoutDueToInactivity = useCallback(() => {
    setStoredSessionExpiredMessage(SESSION_EXPIRED_INACTIVITY_MESSAGE)
    setSessionExpiredMessage(SESSION_EXPIRED_INACTIVITY_MESSAGE)
    saveAuth(undefined)
    setCurrentUser(undefined)
    logoff()
    window.location.href = `${process.env.PUBLIC_URL || ''}/auth`
  }, [saveAuth])

  useInactivityTimer(logoutDueToInactivity, !!auth)

  useEffect(() => {
    const handleActivity = () => {
      const storedAuth = getAuth()
      if (storedAuth?.api_token && isTokenExpired(storedAuth)) {
        logoutDueToInactivity()
      }
    }

    document.addEventListener('click', handleActivity)
    document.addEventListener('keydown', handleActivity)
    document.addEventListener('touchstart', handleActivity)

    return () => {
      document.removeEventListener('click', handleActivity)
      document.removeEventListener('keydown', handleActivity)
      document.removeEventListener('touchstart', handleActivity)
    }
  }, [logoutDueToInactivity])

  const authContextValue = useMemo(
    () => ({
      auth,
      saveAuth,
      currentUser,
      setCurrentUser,
      logout,
      sessionExpiredMessage,
      logoutDueToInactivity,
    }),
    [
      auth,
      saveAuth,
      currentUser,
      setCurrentUser,
      logout,
      sessionExpiredMessage,
      logoutDueToInactivity,
    ]
  )

  return (
    <AuthContext.Provider
      value={authContextValue}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, setCurrentUser, saveAuth} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const storedAuth = getAuth();
          if(storedAuth) {
            const currentDate = new Date().getTime();

            if(storedAuth.expiresAt && currentDate >= storedAuth.expiresAt) {
              saveAuth(undefined);
              setCurrentUser(undefined);
            } else {
              const user:UserModel = {
                organization_primaryEmail: storedAuth.email,
                token: storedAuth.api_token
              }
              setCurrentUser(user);

              getUserDetails(storedAuth.email).then((data) => {
                setCurrentUser(data);
             });

            }
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.api_token) {
      requestUser()
    } else {
      setShowSplashScreen(false)
    }
  }, [auth, saveAuth, setCurrentUser])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
